import React, { useEffect, useState, useCallback } from "react";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { DISTRICTS } from "@/i18n";
import TempleCard from "@/components/TempleCard";
import { DiyaLoader, Toranam } from "@/components/Ornaments";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Temples() {
  const { t } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [district, setDistrict] = useState("all");
  const [pref, setPref] = useState("all");
  const [puro, setPuro] = useState("all");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/temples", { params: { district, preference: pref, purohitam: puro, search } });
      setItems(data);
    } catch (e) {} finally { setLoading(false); }
  }, [district, pref, puro, search]);

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);

  return (
    <div className="parchment-texture min-h-screen">
      <div className="bg-gradient-to-r from-maroon to-saffron py-10 text-center">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">🛕 {t("temples_heading")}</h1>
        <p className="text-white/85 font-telugu mt-2">ఆంధ్రప్రదేశ్ & తెలంగాణలోని దేవాలయ అర్చక ఖాళీలు</p>
      </div>
      <Toranam />

      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-saffron" />
            <Input data-testid="temple-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className="pl-9 bg-surface gold-border font-telugu" />
          </div>
          <Select value={district} onValueChange={setDistrict}>
            <SelectTrigger data-testid="temple-district-filter" className="bg-surface gold-border font-telugu"><SelectValue placeholder={t("all_districts")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("all_districts")}</SelectItem>{DISTRICTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={pref} onValueChange={setPref}>
            <SelectTrigger data-testid="temple-pref-filter" className="bg-surface gold-border font-telugu"><SelectValue placeholder={t("filter_pref")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_prefs")}</SelectItem>
              <SelectItem value="family">👨‍👩‍👦 {t("family_preferred")}</SelectItem>
              <SelectItem value="bachelor">🧘 {t("bachelor_preferred")}</SelectItem>
              <SelectItem value="none">🕉️ {t("no_pref")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={puro} onValueChange={setPuro}>
            <SelectTrigger data-testid="temple-puro-filter" className="bg-surface gold-border font-telugu"><SelectValue placeholder={t("filter_purohitam")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("filter_purohitam")}</SelectItem>
              <SelectItem value="allowed">{t("pref_allowed")}</SelectItem>
              <SelectItem value="not_allowed">{t("pref_not_allowed")}</SelectItem>
              <SelectItem value="after_hours">{t("pref_after")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? <DiyaLoader /> : items.length === 0 ? (
          <div className="text-center py-20 font-telugu text-muted-foreground">ఖాళీలు కనబడలేదు</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="temples-grid">{items.map((tm) => <TempleCard key={tm.id} temple={tm} />)}</div>
        )}
      </div>
    </div>
  );
}
